import { useEffect, useRef } from 'react'
import type { ProjectItem } from '@papan/files/ui'

/**
 * `?ws=&session=` — open one conversation on arrival, then drop the
 * parameters.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 *
 * Another surface wanting to open a conversation had exactly one route in:
 * `queueSessionTab`, which writes the open-tab list that `ChatPanel` reads
 * inside a WORKSPACE view. Neither chat deck is that view — each keeps its
 * own `selected` and never looks at that list — so the member's
 * chat-storage page, which queued and then navigated, landed on the default
 * assistant with nothing open. Reported on production, 2026-10-22:
 * "clicking the conversation name just goes to the home page".
 *
 * ── Why it is a hook and not a copy ──────────────────────────────────────
 *
 * There are two decks — `ChatHomeView` on desktop and `MobileChatScreen` on
 * a phone — and the chat-storage page is reachable on both. The first cut
 * fixed only the desktop one, so a phone kept the reported symptom AND
 * started accumulating a query string nothing stripped. Both call this.
 *
 * ── The shape of the retry ───────────────────────────────────────────────
 *
 * The project list arrives asynchronously, and `openSession` needs a real
 * `ProjectItem` — the hero binds its tools and its title to it, so a
 * synthesised stand-in would show the wrong name and bind the wrong tools.
 * So this waits — on a PENDING FLAG PER QUERY, never on the data. Three
 * versions of this condition got it wrong in three different ways: `[]`
 * read as "still loading" (hung on an empty account), an AND of two
 * unresolved queries (settled the moment either landed), and `undefined`
 * read as "in flight" (hung for ever on an ERRORED list — a failed query
 * has no data either). A flag that is false once its query has settled,
 * success or error, is the only one of the four that cannot do any of
 * them. A PAUSED fetch (offline) still reads as pending, and waiting is
 * right there: the data arrives with the connection, and the only cost
 * meanwhile is a query string left in the address bar.
 */
export function useOpenConversationFromUrl(opts: {
  /** The list, once there is one. `undefined` means "no list" — which is
   *  in-flight AND errored, so it is never the wait signal. */
  allProjects: ProjectItem[] | undefined
  driveAssistant: ProjectItem | null
  /** ⚠️ THE WAIT IS THESE TWO AND NOTHING ELSE, one per query, same shape.
   *  Both `data` values are ambiguous — `undefined` is in-flight or errored,
   *  `null` is asking or absent — and reading the wait off either of them
   *  is what produced both of this hook's bugs: round 2 settled on whichever
   *  query answered first, and round 3's fix for that hung for ever on an
   *  errored project list. A flag per query, settled on success OR error,
   *  is the shape that cannot do either. */
  projectsPending: boolean
  driveAssistantPending: boolean
  openSession: (project: ProjectItem, sessionId: string | null) => void | Promise<void>
  /** Where to land once the parameters are consumed — this tenant's chat. */
  urlPrefix: string
  /** `navigate` from react-router; called with `{ replace: true }`. */
  navigate: (to: string, opts: { replace: true }) => void
}): void {
  const { allProjects, driveAssistant, projectsPending, driveAssistantPending, openSession, urlPrefix, navigate } = opts
  const settled = useRef(false)

  useEffect(() => {
    if (settled.current) return
    const params = new URLSearchParams(window.location.search)
    const ws = params.get('ws')
    const sessionId = params.get('session')
    if (!ws || !sessionId) { settled.current = true; return }

    const project = driveAssistant?.id === ws
      ? driveAssistant
      : allProjects?.find((p) => p.id === ws)
    // Try again when an outstanding query lands. BOTH must have settled
    // before "not found" is an answer, and a query that FAILED counts as
    // settled — otherwise a 500 on the project list leaves the parameters
    // in the address bar for the life of the tab, which is worse than
    // giving up.
    if (!project && (projectsPending || driveAssistantPending)) return

    settled.current = true
    if (project) void openSession(project, sessionId)
    // Dropped whether or not the workspace was found, so a reload does not
    // reopen it and a later click is not fought by a stale URL. Only these
    // two are ours, so the rest of the query string is preserved.
    params.delete('ws')
    params.delete('session')
    const rest = params.toString()
    navigate(`${urlPrefix}/chat${rest ? `?${rest}` : ''}`, { replace: true })
  }, [allProjects, driveAssistant, projectsPending, driveAssistantPending, openSession, urlPrefix, navigate])
}
